import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccountService } from '../../../../Services/Accounts/account.service';
import { FirmService } from '../../../../Services/firm.service';
import { NotificationService } from '../../../../Services/notification.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';

@Component({
  selector: 'app-account-balance',
  imports: [FormsModule, NgFor, NgIf],
  templateUrl: './account-balance.component.html',
  styleUrl: './account-balance.component.css'
})
export class AccountBalanceComponent implements OnInit {

  // cr/dr selection
  crdrTypes: string[] = ['CR', 'DR'];

  // for firm
  firms: any[] = [];
  selectedFirmId: any = '';

  // For Financial year
  years: string[] = [];
  selectedYear: string = '';

  // Accounts data
  accountsData: any[] = [];

  // Totals for "Diff. in Balances"
  totals = {
    opening: 0,
    debit: 0,
    credit: 0,
    closing: 0
  };

  constructor(
    private accountService: AccountService,
    private firmService: FirmService,
    private notification: NotificationService,
    private firmSelectionService: FirmSelectionService
  ) { }

  ngOnInit() {
    this.generateYears();
    this.getFirms();

    // Subscribe to firm selection changes from layout
    this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm) {
        this.selectedFirmId = firm.id;
        this.fetchAccounts();
      }
    });

    // Initial load from localStorage
    const storedFirmId = localStorage.getItem('firm_id');
    if (storedFirmId) {
      this.selectedFirmId = Number(storedFirmId);
    }

    this.fetchAccounts();
  }

  generateYears() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-indexed (April is 3)

    // In India, FY 2025-26 starts Apr 2025 and ends Mar 2026.
    // If currentMonth < 3 (Jan, Feb, Mar), the running FY started last year
    let runningStartYear = currentMonth < 3 ? currentYear - 1 : currentYear;

    this.years = [];
    // Start from 2000 up to the running FY (inclusive)
    for (let sy = 2000; sy <= runningStartYear; sy++) {
      const ey = (sy + 1).toString().slice(-2);
      const yearRange = `${sy}-${ey}`;
      this.years.push(yearRange);
    }
    this.years.reverse();
    if (this.years.length > 0) this.selectedYear = this.years[0];
  }

  getFirms() {
    this.firmService.getFirms().subscribe({
      next: (res: any) => {
        this.firms = res.data;
      },
      error: (err) => {
        console.error('Error fetching firms', err);
      }
    });
  }

  fetchAccounts() {
    let params: any = {
      firm_id: this.selectedFirmId
    };

    // We don't pass from_date/to_date to backend anymore to get all accounts.
    // Instead, we handle the filtering logic in frontend for data display.

    this.accountService.getAccounts(params).subscribe({
      next: (res: any) => {
        this.accountsData = res.data.map((acc: any) => {
          const inRange = this.isDateInYearRange(acc.acc_cash_balance_date, this.selectedYear);
          return {
            ...acc,
            opening_balance: inRange ? (acc.acc_cash_balance || 0) : 0,
            opening_crdr: inRange ? (acc.acc_cash_balance_crdr || 'CR') : 'CR',
            debit_amount: inRange ? (acc.acc_debit_amount || 0) : 0,
            credit_amount: inRange ? (acc.acc_credit_amount || 0) : 0,
            closing_balance: inRange ? (acc.acc_closing_balance || 0) : 0,
            closing_crdr: inRange ? (acc.acc_closing_balance_crdr || 'CR') : 'CR'
          };
        });
        this.calculateDiffs();
      },
      error: (err) => {
        console.error('Error fetching accounts', err);
      }
    });
  }

  isDateInYearRange(dateStr: string, yearRange: string): boolean {
    if (!dateStr || !yearRange) return false;
    const parts = yearRange.split('-');
    const startYear = parseInt(parts[0]);
    const endShortYear = parseInt(parts[1]);
    const endYear = 2000 + endShortYear;

    const date = new Date(dateStr);
    const startDate = new Date(`${startYear}-04-01`);
    const endDate = new Date(`${endYear}-03-31`);

    return date >= startDate && date <= endDate;
  }

  onUpdateAccount(account: any) {
    if (!this.selectedYear) return;

    // Use the first day of the selected fiscal year as the balance date
    const parts = this.selectedYear.split('-');
    const fyStartDate = `${parts[0]}-04-01`;

    const data = {
      acc_cash_balance: account.opening_balance,
      acc_cash_balance_crdr: account.opening_crdr,
      acc_debit_amount: account.debit_amount,
      acc_credit_amount: account.credit_amount,
      acc_closing_balance: account.closing_balance,
      acc_closing_balance_crdr: account.closing_crdr,
      acc_cash_balance_date: fyStartDate
    };

    this.accountService.updateAccount(account.acc_id, data).subscribe({
      next: (res: any) => {
        this.notification.showSuccess('Account updated successfully', 'Success');
        this.fetchAccounts(); // Refresh data
      },
      error: (err) => {
        this.notification.showError('Error updating account', 'Error');
      }
    });
  }

  calculateDiffs() {
    this.totals = {
      opening: 0,
      debit: 0,
      credit: 0,
      closing: 0
    };

    this.accountsData.forEach(acc => {
      // Opening balance: DR is positive, CR is negative
      const opening = Number(acc.opening_balance || 0);
      this.totals.opening += (acc.opening_crdr === 'DR') ? opening : -opening;

      this.totals.debit += Number(acc.debit_amount || 0);
      this.totals.credit += Number(acc.credit_amount || 0);

      // Closing balance: DR is positive, CR is negative
      const closing = Number(acc.closing_balance || 0);
      this.totals.closing += (acc.closing_crdr === 'DR') ? closing : -closing;
    });
  }

  onFilterChange() {
    this.fetchAccounts();
  }

  // account list for add buttons (categories)
  loanAccounts = ['Advance Money', 'Bank OD Acc', 'Loans', 'Saving Scheme', 'Secured Loans', 'Unsecured Loans', 'Loan Taken'];
  stockAccounts = ['Purchase Accounts', 'Purchase Gold', 'Purchase RAW Gold', 'Purchase RAW Metal', 'Purchase RAW Silver', 'Purchase Silver', 'Purchase Stone', 'Sales'];

  shouldShowLoan(accountName: string): boolean {
    if (!accountName) return false;
    const name = accountName.toLowerCase();
    return this.loanAccounts.some(ln => name.includes(ln.toLowerCase()));
  }

  shouldShowStock(accountName: string): boolean {
    if (!accountName) return false;
    const name = accountName.toLowerCase();
    return this.stockAccounts.some(st => name.includes(st.toLowerCase()));
  }
}
